import { ReactNode } from 'react';

import {
  createTheme,
  Theme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      bg: string;
      bgDark: string;
      bgLight: string;
      disabled: string;
      button: string;
      buttonActive: string;
      text: string;
      textActive: string;
      primary: string;
      primaryActive: string;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    custom?: {
      bg?: string;
      bgDark?: string;
      bgLight?: string;
      disabled?: string;
      button?: string;
      buttonActive?: string;
      text?: string;
      textActive?: string;
      primary?: string;
      primaryActive?: string;
    };
  }
}

const baseTheme: Theme = createTheme({
  palette: {
    primary: {
      light: '#4d7ffd',
      main: '#145cfc',
      dark: '#0f47c7',
      contrastText: '#fff',
    },
    mode: 'light',
    background: {
      default: '#fdfdfd',
      paper: '#ffffff',
    },
    text: {
      primary: '#202836',
      secondary: '#757b88',
    },
    error: {
      main: '#f05543',
    },
  },
  custom: {
    bg: '#fdfdfd',
    bgDark: '#f5f5f5',
    bgLight: '#f9fafc',
    disabled: '#c0c4cc',
    button: '#f5f5f5',
    buttonActive: '#eff6ff',
    text: '#757b88',
    textActive: '#202836',
    primary: '#145cfc',
    primaryActive: '#4d7ffd',
  },
});

const primaryMuiTheme: Theme = createTheme(baseTheme, {
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 12,
          lineHeight: 1.5,
          border: `1px solid rgba(0,0,0,.25)`,
          backgroundColor: '#000',
          backgroundImage:
            'linear-gradient(to right,  var(--ctl-background-color-lighter) 0%, var(--ctl-background-color-lighter) 100%)',
        },
      },
      defaultProps: {
        disableInteractive: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          color: baseTheme.custom.textActive,
          backgroundColor: baseTheme.custom.button,
          '&:hover': {
            backgroundColor: baseTheme.custom.buttonActive,
            color: '#ffffff',
          },
        },
        containedPrimary: {
          color: baseTheme.custom.textActive,
          backgroundColor: baseTheme.custom.primary,
          '&:hover': {
            backgroundColor: baseTheme.custom.primaryActive,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.error.main,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          overflowY: 'visible',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: baseTheme.custom.textActive,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          '& .MuiAlert-message ul': {
            padding: 0,
            paddingLeft: baseTheme.spacing(2),
            margin: 0,
          },
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: 'bolder',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          maxWidth: '640px',
          minWidth: '450px',
          padding: `${baseTheme.spacing(4)} ${baseTheme.spacing(3)}`,
          backgroundColor: baseTheme.custom.bg,
          backgroundImage: 'none',
          color: baseTheme.custom.text,
        },
      },
    },
  },
});

export function ThemeProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  return (
    <MuiThemeProvider theme={primaryMuiTheme}>{children}</MuiThemeProvider>
  );
}
